import React from 'react';
import { UseFormReturn, FieldValues, Path } from 'react-hook-form';
import { RadioButton } from '@components/shared/radio-button/RadioButton';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { cn } from '@utils/index';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { format, parseISO } from 'date-fns';
import {
  getCriticalIllnessPriceId,
  PriceSearchConditions,
} from '@features/onboarding/utils/criticalIllnessPriceId';

interface CriticalIllnessCoverageSelectorProps<T extends FieldValues> {
  planId: string;
  plan: InsurancePlan;
  form: UseFormReturn<T>;
  name: string;
  viewMode: 'grid' | 'list' | 'tab';
}

export const CriticalIllnessCoverageSelector = <T extends FieldValues>({
  planId,
  plan,
  form,
  name,
  viewMode,
}: CriticalIllnessCoverageSelectorProps<T>) => {
  const { setData, data } = useOnboardingStore();

  const coverageOptions = [
    { value: 10000, label: viewMode === 'grid' ? '$10k' : '$10,000' },
    { value: 20000, label: viewMode === 'grid' ? '$20k' : '$20,000' },
    { value: 30000, label: viewMode === 'grid' ? '$30k' : '$30,000' },
  ];

  const handleCoverageChange = (optionValue: number) => {
    const sanitizeCategory = (category: string) =>
      category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
    const coverageAmountFieldName = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
    const fieldName = `${criticalIllnessPlanKey.replace('Plan', 'PlanPrice')}`;

    form.setValue(
      coverageAmountFieldName as Path<T>,
      optionValue as T[Path<T>]
    );

    if (plan.category === 'Critical Illness') {
      const planTags = data.planTags || {};
      const planTagsForThisPlan = planTags[plan.uid] || [];

      let primaryAge = 30;
      if (data.applicants && data.applicants.length > 0) {
        const primaryApplicant = data.applicants.find(
          (app) => app.applicant === 'primary'
        );
        if (primaryApplicant && primaryApplicant.dateOfBirth) {
          const birthYear = parseInt(primaryApplicant.dateOfBirth.year);
          const currentYear = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear();
          primaryAge = currentYear - birthYear;
        }
      }

      const dependentsCount = planTagsForThisPlan.filter((tag: string) =>
        tag.includes('dependent')
      ).length;
      const hasSpouse = planTagsForThisPlan.some((tag: string) =>
        tag.includes('spouse')
      );

      const conditions: PriceSearchConditions = {
        coverageAmount: optionValue,
        primaryAge,
        dependentsCount,
        hasSpouse,
      };

      const priceId = getCriticalIllnessPriceId(plan, conditions);
      const validPrice = plan.prices.find((price) => price.id === priceId);
      const newPrice = validPrice?.price || 0;

      setData({
        [fieldName]: newPrice,
        [coverageAmountFieldName]: optionValue,
      });

      form.setValue(fieldName as Path<T>, newPrice as T[Path<T>]);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 py-3 border-gray-200',
        viewMode !== 'tab' && 'border-t'
      )}
    >
      <p className="text-xs font-bold">
        Choose the amount of coverage you would like:
      </p>
      <fieldset className="flex gap-4">
        {coverageOptions.map((option) => {
          const sanitizeCategory = (category: string) =>
            category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
          const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
          const coverageAmountFieldName = `${criticalIllnessPlanKey.replace('Plan', 'PlanCoverageAmount')}`;
          const currentCoverageAmount = form.watch(
            coverageAmountFieldName as Path<T>
          ) as number;
          const isSelectedCoverage = +currentCoverageAmount === option.value;

          return (
            <div key={option.value} className="font-normal">
              <RadioButton
                name={`${coverageAmountFieldName}_${planId}`}
                value={option.value.toString()}
                checked={isSelectedCoverage}
                onChange={() => handleCoverageChange(option.value)}
                label={option.label}
                labelProps={{
                  className: cn('!font-normal', viewMode !== 'list' && '!ml-2'),
                }}
              />
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};
